import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './pledge-cell.css'

// COMPONENTS
import * as Icons from '@icons'
import Avatar from '../avatar'
import Button from '@components/atoms/button'
import PledgeSlotFields from '../pledge-slot-fields'
import Spinner from '@components/atoms/spinner'

// UTILITIES
import * as GroupActions from '../../../routes/group/_localStore/actions'
import { flatten } from '@utils/string-helper'
import { getGroupStatus } from '@utils/temp-helpers'
import { useGroupDispatch, useGroupSettings } from '../../../routes/group/_localStore/_hooks'
import { isNonEmptyString } from '@utils/type-checkers'
import { priceFormatter } from '@utils/number-helper'
import { createResponseObj, VirtualCardAPI } from '@apis'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    group: Group
    pledge: Pledge
    status?: LockingItem
    showStatusFlavor?: boolean
    editable?: boolean
    verified?: boolean
    nonParticipant?: boolean
    showOutline?: boolean
    loading?: boolean
}

const PledgeCell: FC<Props> = (props) => {
    const { className: classProps, group, pledge, status = {} as Partial<LockingItem>, showStatusFlavor = true, editable, verified, nonParticipant, showOutline, loading, children, ...divProps } = props
    const className = flatten(status.status, 'pledge-cell', 'flex flex-row items-center', showOutline ? 'pledge-cell-outline' : '', classProps)
    const { user: clerkUser, isSignedIn } = useUser()
    const { id: pledgeId, chargedToCard = 0 } = pledge
    const [isEdit, setIsEdit] = useState(false)
    const [removingParticipant, setRemovingParticipant] = useState(false)

    const localDispatch = useGroupDispatch()
    const { state } = useGroupSettings()
    const { isEditingParticipant } = state

    const amount = ('amount' in status ? status.amount : pledge.amount) || 0
    const user = ('user' in status ? status.user : pledge.user) || {}

    const { isLocked, groupStatus } = getGroupStatus(group)
    const isOrganizer = isSignedIn ? group.organizer?.id === clerkUser.id : false

    useEffect(() => {
        !!isEdit ? localDispatch(GroupActions.setEditingParticipant(true)) : localDispatch(GroupActions.setEditingParticipant(false))
    }, [isEdit])

    const transactions = useMemo(() => {
        const items = group.transactions
        if (!items) return []

        return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
    }, [group.transactions])
    const hasTransactions = useMemo(() => !!transactions && transactions?.length > 0, [transactions])

    const label = useMemo(() => {

        let _label = 'Unassigned'

        if (!!user) {
            _label = `${user?.givenName || ''} ${user?.familyName || ''}`.trim() || 'Unassigned'
            if (user.givenName === 'undefined') _label = "Unassigned"
            if (user.givenName === '') _label = pledge.name || 'Unassigned'
        }

        return _label
    }, [user])

    const removeRef = useRef<HTMLDialogElement>(null)
    function openConfirmRemove() { if (removeRef.current) removeRef.current.showModal() }
    function closeConfirmRemove() { if (removeRef.current) removeRef.current.close() }

    async function removeParticipant() {
        closeConfirmRemove()
        setRemovingParticipant(true)

        const groupId = group?.id
        console.log("Remove Participant Data:", { groupId, pledgeId })
        if (!isNonEmptyString(groupId) || !isNonEmptyString(pledgeId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId, pledgeId })}`
        })

        await VirtualCardAPI.RemovePledgeRequest.fetch(groupId, pledgeId)

        setRemovingParticipant(false)
        return json('success', { status: 200 })
    }

    return (
        <div className={className} {...divProps}>
            {(!isEdit) ? (<>
                {(removingParticipant || loading) ? (
                    <div className="pledge-loader" style={{ zIndex: 2, borderRadius: 'inherit' }}>
                        <Spinner indicator='circle' />
                    </div>
                ) : (<>
                    {(!!pledge.transactionId || !!pledge.user?.id || !isOrganizer) ? (<>
                        <div className="pledge-icon">
                            {(isOrganizer && status.status === 'purchased' && groupStatus === 'active' && !isLocked) && (<>
                                <div className="remove" onClick={openConfirmRemove}>
                                    <Icons.Close height="8px" width="8px" tint="var(--splitcard-black)" />
                                </div>
                                <dialog ref={removeRef}>
                                    <div>
                                        <div className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                                            <h2>Remove Participant</h2>
                                            <p>Are you sure you want to remove {label} from the group? If removed, their contribution will be deducted from the group funds.</p>
                                            <div className="flex flex-col items-stretch gap-2.5">
                                                <Button theme="primary" onClick={removeParticipant}>Remove</Button>
                                                <Button theme="secondary" formMethod="dialog" onClick={closeConfirmRemove}>Go Back</Button>
                                            </div>
                                        </div>
                                    </div>
                                </dialog>
                            </>)}
                            <Avatar user={user} slotName={pledge.name} />
                        </div>
                        <div className='flexrow items-center gap-4 pledge-label'>
                            <div className=''>
                                <p><strong>{label}</strong>{children}</p>
                                {(groupStatus === 'active' && !isLocked && !!user.id && !!pledge.name) &&
                                    <p className='text-body-2 text-gray-500'>({pledge.name})</p>
                                }
                                {(groupStatus === 'active' && isLocked && !!user.id && pledge.closedAt !== '0001-01-01T00:00:00Z') &&
                                    <p className='text-body-2 text-red-600'><strong>Left Group</strong></p>
                                }
                            </div>
                        </div>
                        {!nonParticipant && <h4 className="pledge-status flex flex-col items-end">
                            {(groupStatus === 'canceled')
                                ? '$0.00'
                                : ((groupStatus === 'active' || groupStatus === 'completed' || groupStatus === 'finalized')
                                    ? hasTransactions && (!!pledge.transactionId || !!pledge.user?.id)
                                        ? !!group.spendAmount
                                            ? `${priceFormatter.format(chargedToCard / 100)} / ${priceFormatter.format(amount / 100)}`
                                            : priceFormatter.format(amount / 100)
                                        : priceFormatter.format(amount / 100)
                                    : priceFormatter.format(chargedToCard / 100))}
                            {(isOrganizer && !pledge.transactionId && !pledge.user?.id && groupStatus === 'active' && !isEditingParticipant) && <a className="text-body-2 text-blue-500" onClick={() => setIsEdit(true)}>Edit</a>}
                        </h4>}
                    </>) : (<>
                        <div className="pledge-icon">
                            {(isOrganizer && status.status === 'purchased' && groupStatus === 'active' && !isLocked) && (<>
                                <div className="remove" onClick={openConfirmRemove}>
                                    <Icons.Close height="8px" width="8px" tint="var(--splitcard-black)" />
                                </div>
                                <dialog ref={removeRef}>
                                    <div>
                                        <div className="temp-card" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem' }}>
                                            <h2>Remove Participant</h2>
                                            <p>Are you sure you want to remove {label} from the group? If removed, their contribution will be deducted from the group funds.</p>
                                            <div className="flex flex-col items-stretch gap-2.5">
                                                <Button theme="primary" onClick={removeParticipant}>Remove</Button>
                                                <Button theme="secondary" formMethod="dialog" onClick={closeConfirmRemove}>Go Back</Button>
                                            </div>
                                        </div>
                                    </div>
                                </dialog>
                            </>)}
                            <Avatar user={user} slotName={pledge.name} />
                        </div>
                        <div className='pledge-label'>
                            <p><strong>{label}{children}</strong></p>
                            <div className='flex flex-col items-end'>
                                {!nonParticipant && <h4 className="pledge-status flex flex-col items-end">
                                    {(groupStatus === 'canceled')
                                        ? '$0.00'
                                        : ((groupStatus === 'active' || groupStatus === 'completed')
                                            ? hasTransactions && (!!pledge.transactionId || !!pledge.user?.id)
                                                ? !!group.spendAmount
                                                    ? `${priceFormatter.format(chargedToCard / 100)} / ${priceFormatter.format(amount / 100)}`
                                                    : priceFormatter.format(amount / 100)
                                                : priceFormatter.format(amount / 100)
                                            : priceFormatter.format(chargedToCard / 100))}
                                </h4>}
                                {(isOrganizer && !pledge.transactionId && !pledge.user?.id && groupStatus === 'active' && !isEditingParticipant) && <a className="text-body-2 text-blue-500" onClick={() => setIsEdit(true)}>Edit</a>}
                            </div>
                        </div>
                    </>)
                    }
                </>)}
            </>) : (<>
                <PledgeSlotFields
                    isEdit={isEdit}
                    pledge={pledge}
                    group={group}
                    onPledgeSubmit={() => console.log('submitted')}
                    onPledgeUpdate={() => setIsEdit(false)}
                    onPledgeRemove={() => setIsEdit(false)}
                />
            </>)}
        </div>
    )
}

export default PledgeCell
